import { editKindOfRun, isEditScreenRun } from 'Pods/ThreeDStudio/Shared/Utils/imageRun'

// Edit runs carry no parent-run link, so the chain is reconstructed from their assets:
// the base image is input_image[0] (masked edits add the overlay as [1]) and the output
// becomes the next edit's base.
const baseAssetOf = run => run.inputAssets?.[0]
const outputAssetOf = run => run.outputAssets?.[0]
// producedBy is the run that generated the asset (undefined for the original); it is
// what tells a version's kind (brush/prompt), the instruction that made it and the run
// a delete of that version removes.
const assetEntry = (asset, producedBy) => ({
  id: asset.assetId,
  url: asset.fileUrl,
  kind: producedBy ? editKindOfRun(producedBy) : undefined,
  prompt: producedBy?.prompt,
  runId: producedBy?.id,
})

/**
 * Rebuilds the whole version lineage an image belongs to, by walking the session's
 * edits (masked and prompt) both ways from it: back to the image nothing produced, and
 * forward through the edits made from it. Applying a version and reopening the tab
 * loads that version, so walking forward alone would drop everything it was derived from.
 *
 * Labelling is left to the caller, which has the translator.
 *
 * @param {Array<Object>} generations - Session image runs in creation order (oldest first)
 * @param {number|string} assetId - Asset id of the image loaded onto the canvas
 * @returns {Array<Object>} The lineage oldest first as { id, url, kind, prompt, runId },
 *   starting with the original and including the loaded image; empty when the image
 *   has no edits at all
 */
export const buildVersionChain = (generations, assetId) => {
  if (assetId == null) {
    return []
  }

  const edits = generations.filter(run => (
    isEditScreenRun(run)
    && run.status === 'succeeded'
    && outputAssetOf(run)?.fileUrl
    && baseAssetOf(run)?.assetId != null
  ))

  // Last writer wins in byBase, so a base edited more than once resolves to its newest
  // edit - the branch the user worked on most recently. byOutput needs no such rule: an
  // asset is the output of at most one edit.
  const byBase = new Map()
  const byOutput = new Map()
  edits.forEach((run) => {
    byBase.set(String(baseAssetOf(run).assetId), run)
    byOutput.set(String(outputAssetOf(run).assetId), run)
  })

  // The visited guards stop a cycle from looping forever if an edit ever reports its own
  // base as its output.
  const ancestors = []
  const walkedBack = new Set()
  let cursor = String(assetId)
  while (byOutput.has(cursor) && !walkedBack.has(cursor)) {
    walkedBack.add(cursor)
    const base = baseAssetOf(byOutput.get(cursor))
    ancestors.unshift(assetEntry(base, byOutput.get(String(base.assetId))))
    cursor = String(base.assetId)
  }

  const descendants = []
  const walkedForward = new Set()
  cursor = String(assetId)
  while (byBase.has(cursor) && !walkedForward.has(cursor)) {
    walkedForward.add(cursor)
    const run = byBase.get(cursor)
    const output = outputAssetOf(run)
    descendants.push(assetEntry(output, run))
    cursor = String(output.assetId)
  }

  if (ancestors.length === 0 && descendants.length === 0) {
    return []
  }

  // The loaded image sits between the two walks. Its url comes from whichever run
  // referenced it - as an output when it was edited into existence, as a base otherwise.
  const producedBy = byOutput.get(String(assetId))
  const loaded = producedBy
    ? assetEntry(outputAssetOf(producedBy), producedBy)
    : assetEntry(baseAssetOf(byBase.get(String(assetId))), undefined)

  return [...ancestors, loaded, ...descendants]
}
